/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { take } from 'rxjs/operators';
import { L10nService } from '@vmw/ngx-vip';
import moment from 'moment';
import * as globalL10n from 'global-l10n';
import {
    HOVER_OVERLAY_BOTTOM_POSITION,
    HOVER_OVERLAY_TOP_POSITION,
} from 'ng/modules/tooltip/components/avi-hover-overlay/avi-hover-overlay.constants';
import { IApplicationLog } from 'generated-types';
import { LogsType } from '../../logs.types';
import { TTrafficLog } from '../traffic-logs/traffic-logs.types';
import { LogsDurationDetailService } from '../../services/logs-duration-detail.service';

import * as l10n from './log-duration-detail.l10n';
import './log-duration-detail.component.less';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs/traffic-logs.store';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      Tooltip component that shows breakdown of traffic log duration properties.
 * @author Alex Klyuev, Zhiqian Liu
 */
@Component({
    selector: '[logDurationDetail]',
    templateUrl: './log-duration-detail.component.html',
})
export class LogDurationDetailComponent implements OnInit {
    /**
     * Log object.
     */
    @Input()
    public log: TTrafficLog;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * Enum for use in view.
     */
    public readonly LogsType = LogsType;

    /**
     * If any time is less than 1 ms, we display < 1ms.
     */
    public readonly minDisplayTime = 1;

    /**
     * Computed log duration properties.
     */
    public clientRtt: TTrafficLog['client_rtt'];

    public serverRtt: TTrafficLog['server_rtt'];

    public wafTime?: number;

    public appResponseTime?: IApplicationLog['app_response_time'];

    public dataTransferTime?: IApplicationLog['data_transfer_time'];

    public totalTime: TTrafficLog['total_time'];

    /**
     * Formatted log duration properties.
     */
    public startTime: string;

    public clientRttFormatted: string;

    public serverRttFormatted: string;

    public wafTimeFormatted: string;

    public appResponseTimeFormatted: string;

    public dataTransferTimeFormatted: string;

    public totalTimeFormatted: string;

    /**
     * Position for overlay.
     */
    public hoverOverlayPositionPriorities: ConnectedPosition[] = [
        HOVER_OVERLAY_BOTTOM_POSITION,
        HOVER_OVERLAY_TOP_POSITION,
    ];

    constructor(
        l10nService: L10nService,
        public readonly logsStore: TrafficLogsStore<ITrafficLogsStates>,
        @Inject('$filter')
        private readonly timeFormatterService: any,
        private readonly logsDurationDetailService: LogsDurationDetailService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.clientRtt = this.log.client_rtt;
        this.serverRtt = this.log.server_rtt;
        this.totalTime = this.log.total_time;

        this.startTime = moment(this.log.report_timestamp)
            .subtract(this.totalTime, 'ms').toString();

        this.clientRttFormatted = this.timeFormatterService('ms2str')(this.clientRtt, true);
        this.serverRttFormatted = this.timeFormatterService('ms2str')(this.serverRtt, true);
        this.totalTimeFormatted = this.timeFormatterService('ms2str')(this.totalTime, true);

        this.logsStore.logsType$.pipe(take(1)).subscribe(type => {
            if (type === LogsType.APPLICATION) {
                const {
                    app_response_time: appResponseTime,
                    data_transfer_time: dataTransferTime,
                } = this.log as IApplicationLog;

                this.appResponseTime = appResponseTime;
                this.dataTransferTime = dataTransferTime;

                this.appResponseTimeFormatted =
                    this.timeFormatterService('ms2str')(this.appResponseTime, true);

                this.dataTransferTimeFormatted =
                    this.timeFormatterService('ms2str')(this.dataTransferTime, true);
            }
        });

        this.logsStore.hasWafPolicy$.pipe(take(1)).subscribe(hasWafPolicy => {
            if (hasWafPolicy) {
                this.wafTime = this.logsDurationDetailService.getWafTime(
                    this.log as IApplicationLog,
                );
                this.wafTimeFormatted = this.timeFormatterService('ms2str')(this.wafTime, true);
            }
        });
    }
}
